import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { graphql } from "gatsby";
import Layout from "../components/Layout";

import Stage from "../components/Stage/Stage";
import ServiceOverview from "../components/ServiceOverview/ServiceOverview";
import ServiceTeaser from "../components/ServiceTeaser/ServiceTeaser";
import ProductTeaser from "../components/ProductTeaser/ProductTeaser";
import ScrollArrow from "../components/share/ScrollArrow/ScrollArrow";
import Container from "../components/share/Container/Container";
import Button from "../components/share/Button/Button";
import "./service-page.scss";
import useScrollPos from "../hooks/useScrollPos";
import useBreakpoint from "../hooks/useBreakpoint";

const BRIDGE_PHASES = [
  {
    number: "01",
    label: "Planung",
    text: "Beratung im Showroom, Vor-Ort-Termin in Ihrem Garten und ein transparentes Angebot.",
  },
  {
    number: "02",
    label: "Bauphase",
    text: "Erdaushub, Bodenplatte, Anlieferung des Beckens, Verrohrung und Hinterfüllung.",
  },
  {
    number: "03",
    label: "Abnahme",
    text: "Gemeinsame Inbetriebnahme und Einweisung in Steuerung und Wasserpflege.",
  },
];

export const ServicePageTemplate = ({
  title,
  image,
  description,
  sOverview,
  laborArea,
  shop,
}) => {
  return (
    <div className="ServicePage">
      <div className="ServicePage__stage">
        <Stage title={title} image={image} />
      </div>
      <div className="ServicePage__content">
        <ServiceOverview services={sOverview} description={description} />
        <div className="PoolbauBridge">
          <Container variant="no-top">
            <div className="PoolbauBridge__head">
              <div className="PoolbauBridge__intro">
                <p className="PoolbauBridge__eyebrow">Der Weg zum eigenen Pool</p>
                <h2 className="PoolbauBridge__title">Planung &middot; Bauphase &middot; Abnahme</h2>
                <p className="PoolbauBridge__sub">
                  Von der ersten Idee bis zum ersten Sprung ins kühle Nass — wir begleiten Sie transparent durch jeden Schritt.
                </p>
              </div>
              <div className="PoolbauBridge__action">
                <Button link="/poolbau" label="Zum Poolbau-Prozess" />
              </div>
            </div>
            <ol className="PoolbauBridge__phases">
              {BRIDGE_PHASES.map((phase) => (
                <li className="PoolbauBridge__phase" key={phase.number}>
                  <span className="PoolbauBridge__phaseNumber">{phase.number}</span>
                  <h3 className="PoolbauBridge__phaseLabel">{phase.label}</h3>
                  <p className="PoolbauBridge__phaseText">{phase.text}</p>
                </li>
              ))}
            </ol>
          </Container>
        </div>
        <ServiceTeaser content={laborArea} />
        <ProductTeaser
          title={shop.title}
          images={[shop.imageObject]}
          description={shop.text}
          link="/contact"
          linkLabel="Vereinbaren Sie einen Termin"
        />
      </div>
    </div>
  );
};

ServicePageTemplate.propTypes = {
  image: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  title: PropTypes.string,
  description: PropTypes.string,
  sOverview: PropTypes.array,
  laborArea: PropTypes.object,
  shop: PropTypes.object,
};

const ServicePage = ({ data, location }) => {
  const { frontmatter } = data.markdownRemark;

  const [whiteArrow, setWhiteArrow] = useState(false);
  const currentScrollY = useScrollPos();
  const BreakpointM = useBreakpoint("m");

  useEffect(() => {
    if (BreakpointM && currentScrollY < 100) {
      setWhiteArrow(true);
    } else {
      setWhiteArrow(false);
    }
  }, [currentScrollY, BreakpointM]);

  return (
    <Layout pageTitle={frontmatter.metaTitle} pageDescription={frontmatter.metaDescription} pagePath={location && location.pathname}>
      <ServicePageTemplate
        image={frontmatter.image}
        title={frontmatter.title}
        description={frontmatter.description}
        sOverview={frontmatter.sOverview}
        laborArea={frontmatter.laborArea}
        shop={frontmatter.shop}
      />
      <ScrollArrow color={whiteArrow ? "white" : ""} />
    </Layout>
  );
};

ServicePage.propTypes = {
  data: PropTypes.shape({
    markdownRemark: PropTypes.shape({
      frontmatter: PropTypes.object,
    }),
  }),
};

export default ServicePage;

export const ServicePageQuery = graphql`
  query ServicePage {
    markdownRemark(frontmatter: { templateKey: { eq: "service-page" } }) {
      frontmatter {
        title
        image {
          childImageSharp {
            gatsbyImageData(
              placeholder: BLURRED
              formats: [AUTO, WEBP]
              width: 2000
              breakpoints: [480, 568, 800, 1024, 1280]
              quality: 100
            )
          }
        }
        description
        sOverview {
          alt
          image {
            publicURL
          }
          text
          title
          link
        }
        shop {
          text
          title
          imageObject {
            alt
            image {
              childImageSharp {
                gatsbyImageData(
                  placeholder: BLURRED
                  formats: [AUTO, WEBP]
                  width: 2000
                  breakpoints: [480, 568, 800, 1024, 1280]
                  quality: 100
                )
              }
            }
          }
        }
        laborArea {
          title
          labor {
            alt
            image {
              publicURL
            }
            text
            title
          }
        }
        metaTitle
        metaDescription
      }
    }
  }
`;
